import Card from 'components/card';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProductions } from 'interfaces/productions';
import { MdFactory, MdCheckCircle, MdSchedule, MdHourglassEmpty, MdWarning, MdCalendarToday, MdLocationOn, MdQrCode } from 'react-icons/md';
import { dateToFrench } from 'utils/date';
import { getIcon } from 'utils/icons';
import GoBack from 'components/actions/GoBack';
import fetchClient from 'interfaces/fetchClient';


const ProductionHistory = () => {
    const [productions, setProductions] = useState<any>([]);
    const [config, setConfig] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedProduction, setSelectedProduction] = useState<any>(null);
    const [filter, setFilter] = useState<string>('all');
    const nav = useNavigate();

    const initProductions = async () => {
        setIsLoading(true);
        const prods = await getProductions();
        if (!prods) {
            nav('/auth/login');
            return;
        }
        setProductions(prods.data);
        
        // Fetch config for custom field names
        try {
            const response = await fetchClient.get('/productions/config/workflow', {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('token'),
                },
            });
            setConfig(response.data);
        } catch (error) {
            console.error('Error fetching config:', error);
        }
        
        setIsLoading(false);
    };

    useEffect(() => {
        initProductions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getStatusInfo = (status: string) => {
        switch (status) {
            case 'scheduled':
                return {
                    label: 'Planifiée',
                    icon: MdSchedule,
                    color: 'text-green-600',
                    bg: 'bg-green-100',
                };
            case 'custom_step':
                return {
                    label: 'En cours',
                    icon: MdHourglassEmpty,
                    color: 'text-blue-600',
                    bg: 'bg-blue-100',
                };
            case 'pending_validation':
                return {
                    label: 'En validation',
                    icon: MdWarning,
                    color: 'text-orange-600',
                    bg: 'bg-orange-100',
                };
            case 'done':
                return {
                    label: 'Terminée',
                    icon: MdCheckCircle,
                    color: 'text-green-600',
                    bg: 'bg-green-100',
                };
            default:
                return {
                    label: 'Inconnu',
                    icon: MdFactory,
                    color: 'text-gray-600',
                    bg: 'bg-gray-100',
                };
        }
    };

    const getFieldLabel = (fieldKey: string): string => {
        if (!config || !config.customFields) return fieldKey;
        const field = config.customFields.find((f: any) => f.key === fieldKey);
        return field ? field.label : fieldKey;
    };

    const filteredProductions = productions.filter((p: any) => {
        if (filter === 'all') return true;
        return p.status === filter;
    });

    const statusCounts = {
        all: productions.length,
        scheduled: productions.filter((p: any) => p.status === 'scheduled').length,
        custom_step: productions.filter((p: any) => p.status === 'custom_step').length,
        pending_validation: productions.filter((p: any) => p.status === 'pending_validation').length,
        done: productions.filter((p: any) => p.status === 'done').length,
    };

    return (
        <div className="w-full h-full min-h-[85vh]">
            <GoBack />
            
            {/* Header Section */}
            <Card extra="w-full p-5 mb-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-bold text-navy-700 dark:text-white">
                            Historique des productions
                        </h2>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                            Vue d'ensemble de toutes vos productions et leurs étapes
                        </p>
                    </div>
                </div>
            </Card>

            {/* Filters */}
            <Card extra="w-full p-4 mb-4">
                <div className="flex gap-2 overflow-x-auto">
                    <button
                        onClick={() => setFilter('all')}
                        className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                            filter === 'all'
                                ? 'bg-toola-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-navy-800 dark:text-gray-300'
                        }`}
                    >
                        Toutes ({statusCounts.all})
                    </button>
                    <button
                        onClick={() => setFilter('scheduled')}
                        className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                            filter === 'scheduled'
                                ? 'bg-green-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-navy-800 dark:text-gray-300'
                        }`}
                    >
                        Planifiées ({statusCounts.scheduled})
                    </button>
                    <button
                        onClick={() => setFilter('custom_step')}
                        className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                            filter === 'custom_step'
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-navy-800 dark:text-gray-300'
                        }`}
                    >
                        En cours ({statusCounts.custom_step})
                    </button>
                    <button
                        onClick={() => setFilter('pending_validation')}
                        className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                            filter === 'pending_validation'
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-navy-800 dark:text-gray-300'
                        }`}
                    >
                        En validation ({statusCounts.pending_validation})
                    </button>
                    <button
                        onClick={() => setFilter('done')}
                        className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                            filter === 'done'
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-navy-800 dark:text-gray-300'
                        }`}
                    >
                        Terminées ({statusCounts.done})
                    </button>
                </div>
            </Card>

            {/* Productions table */}
            <Card extra="w-full p-5">
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-20">
                        <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-toola-500"></div>
                        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Chargement...</p>
                    </div>
                ) : filteredProductions.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20">
                        <MdFactory className="h-16 w-16 text-gray-400" />
                        <p className="mt-4 text-base font-bold text-gray-700 dark:text-gray-300">
                            Aucune production trouvée
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {filteredProductions.map((production: any) => {
                            const statusInfo = getStatusInfo(production.status);
                            const StatusIcon = statusInfo.icon;
                            const isExpanded = selectedProduction?._id === production._id;

                            return (
                                <div
                                    key={production._id}
                                    className="rounded-lg border border-gray-200 bg-white dark:border-navy-700 dark:bg-navy-800 overflow-hidden transition-all hover:shadow-md"
                                >
                                    {/* Main row - clickable */}
                                    <div
                                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-navy-700"
                                        onClick={() => setSelectedProduction(isExpanded ? null : production)}
                                    >
                                        <div className="flex items-center gap-4 flex-1">
                                            {/* Image */}
                                            <img
                                                className="h-16 w-16 rounded-lg object-contain"
                                                src={getIcon(production.productName, 'product')}
                                                alt={production.productName}
                                            />
                                            
                                            {/* Main info */}
                                            <div className="flex-1">
                                                <h3 className="text-base font-bold text-navy-700 dark:text-white">
                                                    {production.productName === 'Product not found'
                                                        ? "⚠️ Produit introuvable"
                                                        : production.productName}
                                                </h3>
                                                <div className="mt-1 flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                                                    <span className="flex items-center gap-1">
                                                        <MdFactory className="h-4 w-4" />
                                                        {production.quantity} unité{production.quantity > 1 ? 's' : ''}
                                                    </span>
                                                    {production.date && (
                                                        <span className="flex items-center gap-1">
                                                            <MdCalendarToday className="h-4 w-4" />
                                                            {dateToFrench(production.date)}
                                                        </span>
                                                    )}
                                                    {production.lotNumber && (
                                                        <span className="flex items-center gap-1">
                                                            <MdQrCode className="h-4 w-4" />
                                                            {production.lotNumber}
                                                        </span>
                                                    )}
                                                    {production.area && (
                                                        <span className="flex items-center gap-1">
                                                            <MdLocationOn className="h-4 w-4" />
                                                            {production.area.toUpperCase()}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Status */}
                                            <div className={`flex items-center gap-2 rounded-full px-4 py-2 ${statusInfo.bg}`}>
                                                <StatusIcon className={`h-5 w-5 ${statusInfo.color}`} />
                                                <span className={`text-sm font-semibold ${statusInfo.color}`}>
                                                    {statusInfo.label}
                                                </span>
                                            </div>

                                            {/* Current step */}
                                            <div className="text-right">
                                                <p className="text-xs text-gray-500 dark:text-gray-400">Étape</p>
                                                <p className="text-lg font-bold text-navy-700 dark:text-white">
                                                    {production.currentStepIndex || 1}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Expandable details */}
                                    {isExpanded && (
                                        <div className="border-t border-gray-200 bg-gray-50 p-4 dark:border-navy-600 dark:bg-navy-700">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {/* Comment */}
                                                {production.comment && (
                                                    <div className="col-span-full rounded-lg bg-white p-3 dark:bg-navy-800">
                                                        <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                                            Commentaire
                                                        </p>
                                                        <p className="text-sm text-gray-700 dark:text-gray-300">
                                                            {production.comment}
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Ingredients */}
                                                {production.ingredients && production.ingredients.length > 0 && (
                                                    <div className="rounded-lg bg-white p-3 dark:bg-navy-800">
                                                        <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                                                            Ingrédients ({production.ingredients.length})
                                                        </p>
                                                        <div className="space-y-1">
                                                            {production.ingredients.map((ing: any, idx: number) => (
                                                                <div key={idx} className="flex items-center justify-between text-sm">
                                                                    <span className="text-gray-700 dark:text-gray-300">{ing.name}</span>
                                                                    <span className="font-medium text-navy-700 dark:text-white">
                                                                        {ing.quantity}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Custom data */}
                                                {production.customData && Object.keys(production.customData).length > 0 && (
                                                    <div className="rounded-lg bg-white p-3 dark:bg-navy-800">
                                                        <p className="text-xs font-medium text-purple-600 dark:text-purple-400 mb-2">
                                                            Données de production
                                                        </p>
                                                        <div className="space-y-1.5">
                                                            {Object.entries(production.customData).map(([key, value]: [string, any]) => (
                                                                <div key={key} className="flex items-center justify-between text-sm">
                                                                    <span className="text-gray-600 dark:text-gray-400">
                                                                        {getFieldLabel(key)}:
                                                                    </span>
                                                                    <span className="font-semibold text-navy-700 dark:text-white">
                                                                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Best before date */}
                                                {production.DLC && (
                                                    <div className="rounded-lg bg-white p-3 dark:bg-navy-800">
                                                        <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                                            Date limite de consommation
                                                        </p>
                                                        <p className="text-sm font-semibold text-navy-700 dark:text-white">
                                                            {dateToFrench(production.DLC)}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </Card>
        </div>
    );
};

export default ProductionHistory;
